import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Activity,
  Coins,
  Loader2,
  Users,
  Wallet,
} from "lucide-react";
import {
  fetchBondDrawInvestors,
  fetchBondDrawPlayers,
  fetchBondDrawPool,
  selectBondDrawInvestors,
  selectBondDrawLoadingInvestors,
  selectBondDrawLoadingPlayers,
  selectBondDrawPlayers,
  selectBondDrawPool,
} from "../../redux/bondDrawSlice";
import { formatDrawCoin } from "../../utils/bondDrawHelpers";
import BondDrawPlayerCard from "./BondDrawPlayerCard";
import BondDrawInvestorCard from "./BondDrawInvestorCard";
import BondDrawInvestorTabActions from "./BondDrawInvestorTabActions";

const BondDrawSidePanel = ({ onAddBalance, onToast }) => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("players");

  const players = useSelector(selectBondDrawPlayers);
  const investors = useSelector(selectBondDrawInvestors);
  const pool = useSelector(selectBondDrawPool);
  const loadingPlayers = useSelector(selectBondDrawLoadingPlayers);
  const loadingInvestors = useSelector(selectBondDrawLoadingInvestors);
  const currentUserId = useSelector((state) => state.appAuth?.user?._id);

  useEffect(() => {
    dispatch(fetchBondDrawPool());
    dispatch(fetchBondDrawPlayers({ limit: 100 }));
    dispatch(fetchBondDrawInvestors({ limit: 100 }));
  }, [dispatch]);

  useEffect(() => {
    if (activeTab === "players") dispatch(fetchBondDrawPlayers({ limit: 100 }));
    if (activeTab === "investors") dispatch(fetchBondDrawInvestors({ limit: 100 }));
  }, [activeTab, dispatch]);

  const sortedPlayers = useMemo(() => {
    const arr = [...(players || [])];
    arr.sort((a, b) => {
      if (currentUserId) {
        const aMine = String(a.userId) === String(currentUserId);
        const bMine = String(b.userId) === String(currentUserId);
        if (aMine && !bMine) return -1;
        if (!aMine && bMine) return 1;
      }
      return (b.won + b.pending - b.lost) - (a.won + a.pending - a.lost);
    });
    return arr;
  }, [players, currentUserId]);

  const sortedInvestors = useMemo(() => {
    const arr = [...(investors || [])];
    arr.sort((a, b) => {
      if (currentUserId) {
        const aMine = String(a.userId) === String(currentUserId);
        const bMine = String(b.userId) === String(currentUserId);
        if (aMine && !bMine) return -1;
        if (!aMine && bMine) return 1;
      }
      return (b.active || 0) - (a.active || 0);
    });
    return arr;
  }, [investors, currentUserId]);

  const isCurrentUserInvestor = useMemo(() => {
    if (!currentUserId) return false;
    return sortedInvestors.some((inv) => String(inv.userId) === String(currentUserId));
  }, [sortedInvestors, currentUserId]);

  const currentInvestor = useMemo(() => {
    if (!currentUserId) return null;
    return sortedInvestors.find((inv) => String(inv.userId) === String(currentUserId)) || null;
  }, [sortedInvestors, currentUserId]);

  const renderTab = (key, label, icon, count) => {
    const isActive = activeTab === key;
    return (
      <button
        key={key}
        type="button"
        className={`bdraw-tab${isActive ? " active" : ""}`}
        onClick={() => setActiveTab(key)}
        aria-selected={isActive}
        role="tab"
      >
        {icon}
        <span>{label}</span>
        <span className="bdraw-tab-count">{count}</span>
      </button>
    );
  };

  const renderPool = () => {
    const active = Number(pool?.activeTotal || 0);
    const reserved = Number(pool?.reservedTotal || 0);
    const total = active + reserved;
    const activePct = total > 0 ? (active / total) * 100 : 0;
    const investorsCount = Number(pool?.investorsCount || 0);
    const activeInvestors = Number(pool?.activeInvestors || 0);

    return (
      <div className="bdraw-pool-card">
        <div className="bdraw-pool-head">
          <span className="bdraw-pool-kicker">
            <Activity size={11} strokeWidth={2.5} />
            Investor pool
          </span>
          <span className="bdraw-pool-investors">
            {activeInvestors}/{investorsCount} active
          </span>
        </div>
        <strong className="bdraw-pool-amount">{formatDrawCoin(active)} <em>COIN</em></strong>
        <div className="bdraw-pool-bar">
          <span className="bdraw-pool-bar-active" style={{ width: `${activePct}%` }} />
          <span className="bdraw-pool-bar-reserved" style={{ width: `${100 - activePct}%` }} />
        </div>
        <div className="bdraw-pool-meta">
          <span>
            <span className="bdraw-pool-dot active" /> {formatDrawCoin(active)} active
          </span>
          <span>
            <span className="bdraw-pool-dot reserved" /> {formatDrawCoin(reserved)} reserved
          </span>
        </div>
      </div>
    );
  };

  const renderPlayers = () => {
    if (loadingPlayers && sortedPlayers.length === 0) {
      return (
        <div className="bdraw-panel-loading">
          <Loader2 size={18} className="bdraw-spin" />
          <span>Loading players…</span>
        </div>
      );
    }
    if (sortedPlayers.length === 0) {
      return (
        <div className="bdraw-panel-empty">
          <Users size={22} strokeWidth={2} />
          <p>No players yet</p>
          <span>Be the first to reserve a Bond Draw ticket.</span>
        </div>
      );
    }
    return (
      <ul className="bdraw-panel-list bdraw-player-list">
        {sortedPlayers.map((player) => (
          <BondDrawPlayerCard
            key={player.userId}
            player={player}
            isCurrentUser={currentUserId && String(player.userId) === String(currentUserId)}
          />
        ))}
      </ul>
    );
  };

  const renderInvestorTabActions = () => {
    if (!currentUserId || !onAddBalance) return null;
    return (
      <BondDrawInvestorTabActions
        investor={isCurrentUserInvestor ? currentInvestor : null}
        onAddBalance={onAddBalance}
        onToast={onToast}
      />
    );
  };

  const renderInvestors = () => {
    const tabActions = renderInvestorTabActions();

    if (loadingInvestors && sortedInvestors.length === 0) {
      return (
        <>
          {tabActions}
          <div className="bdraw-panel-loading">
            <Loader2 size={18} className="bdraw-spin" />
            <span>Loading investors…</span>
          </div>
        </>
      );
    }
    if (sortedInvestors.length === 0) {
      return (
        <>
          {tabActions}
          <div className="bdraw-panel-empty">
            <Wallet size={22} strokeWidth={2} />
            <p>No investors yet</p>
            <span>Investors fund the prize pool that powers each draw.</span>
          </div>
        </>
      );
    }
    return (
      <>
        {tabActions}
        <ul className="bdraw-panel-list bdraw-player-list">
          {sortedInvestors.map((inv) => (
            <BondDrawInvestorCard
              key={inv.userId}
              investor={inv}
              isCurrentUser={currentUserId && String(inv.userId) === String(currentUserId)}
              onToast={onToast}
            />
          ))}
        </ul>
      </>
    );
  };

  return (
    <aside className="bdraw-side" aria-label="Bond Draw activity">
      {renderPool()}
      <div className="bdraw-tabs" role="tablist">
        {renderTab("players", "Players", <Users size={12} strokeWidth={2.5} />, sortedPlayers.length)}
        {renderTab("investors", "Investors", <Coins size={12} strokeWidth={2.5} />, sortedInvestors.length)}
      </div>
      <div className="bdraw-panel-body" role="tabpanel">
        {activeTab === "players" ? renderPlayers() : renderInvestors()}
      </div>
    </aside>
  );
};

export default BondDrawSidePanel;
